import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import GoogleButton, { useGoogleEnabled } from "../components/GoogleButton";
import { safeNext } from "../lib/safeNext";

/**
 * Pick the correct post-login destination.
 *  - Farmer default: /farm-dashboard
 *  - Customer default: /dashboard
 *  - If an explicit ?redirect= (or state.from) was passed AND it belongs to
 *    the signed-in user's role, honour it. Otherwise fall back to the role
 *    default so a farmer never lands on the customer dashboard and a
 *    customer can't be redirected into a farmer-only page.
 */
function pickDestination(user, requestedFrom) {
  const isFarmer = !!user?.has_farm;
  const roleDefault = isFarmer ? "/farm-dashboard" : "/dashboard";
  if (!requestedFrom) return roleDefault;
  const looksFarmerOnly = requestedFrom.startsWith("/farm-dashboard")
    || requestedFrom.startsWith("/farm/dashboard")
    || requestedFrom.startsWith("/farmer/");
  const looksCustomerOnly = requestedFrom === "/dashboard"
    || requestedFrom.startsWith("/dashboard/");
  if (isFarmer && looksCustomerOnly) return roleDefault;
  if (!isFarmer && looksFarmerOnly) return roleDefault;
  return requestedFrom;
}

export default function Login() {
  const { login, loginWithToken, isAuthenticated, user, loading: authLoading } = useAuth();
  const googleOn = useGoogleEnabled();
  // Anonymous first-party login-device cookie (HttpOnly, server-signed) used only to scope sign-in rate limits.
  useEffect(() => { axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/device`).catch(() => {}); }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: new URLSearchParams(location.search).get("email") || "",
    password: ""
  });
  const [paused, setPaused] = useState(null);

  const requestedFrom = safeNext(new URLSearchParams(location.search).get("redirect") || location.state?.from || null);
  const linkToken = new URLSearchParams(location.search).get("link");
  const needsPassword = new URLSearchParams(location.search).get("setpw") === "1";
  const linkEmail = new URLSearchParams(location.search).get("email") || "";

  // If the auth context is still resolving, hold the page so the customer
  // dashboard doesn't briefly flash for a farmer whose role is not yet loaded.
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="login-resolving">
        <div className="animate-pulse h-8 w-32 bg-heritage-navy/20 rounded" />
      </div>
    );
  }

  // Already signed in — route by role immediately.
  if (isAuthenticated && user) {
    return <Navigate to={pickDestination(user, requestedFrom)} replace />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const email = formData.email.trim();
    try {
      let signedInUser;
      if (linkToken) {
        // Proves ownership with this account's password, then connects Google to the SAME account.
        const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/google/link`, { link_token: linkToken, email, password: formData.password });
        loginWithToken(data.token, data.user);
        signedInUser = data.user;
        toast.success("Google sign-in is now connected to your account.");
      } else {
        signedInUser = (await login(email, formData.password)).user;
      }
      navigate(pickDestination(signedInUser, requestedFrom), { replace: true });
    } catch (error) {
      const st = error?.response?.status; const d = error?.response?.data?.detail;
      const msg = typeof d === "object" ? d?.message : null;
      if (d?.code === "login_paused") setPaused(msg);
      else if (msg) {
        toast.error(msg);
        if (d?.code === "link_expired") navigate(`/login${requestedFrom ? `?redirect=${encodeURIComponent(requestedFrom)}` : ""}`, { replace: true });
      }
      else if (st === 429 || st === 409) toast.error(d);
      else if (st === 401) toast.error("That email and password don't match. If you usually use Google, continue with Google — or reset your password below.");
      else toast.error("We couldn't sign you in just now. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20 lg:pt-24 flex items-center" data-testid="login-page">
      <div className="w-full max-w-sm mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <h1 
            className="text-2xl lg:text-3xl mb-3"
            style={{ color: '#0f1d2f', fontFamily: "'GFS Didot', 'Didot', Georgia, serif" }}
          >
            Welcome Back
          </h1>
          <p className="text-sm" style={{ color: '#0f1d2f' }}>
            Your American Farmstand awaits.
          </p>
          {linkToken && (
            <p className="text-sm mt-6 px-4 py-3 border text-left" style={{ color: '#0f1d2f', borderColor: 'rgba(182, 164, 109, 0.5)', backgroundColor: 'rgba(182, 164, 109, 0.08)' }} data-testid="login-google-link-notice">
              An American Farmstand account already uses {linkEmail || "this email"}. Enter that account's password once to connect Google — your membership, orders and saved items stay exactly as they are. Next time you can simply continue with Google.
            </p>
          )}
          {linkToken && needsPassword && (
            <p className="text-sm mt-3 px-4 py-3 border text-left" style={{ color: '#0f1d2f', borderColor: 'rgba(182, 164, 109, 0.5)' }} data-testid="login-link-setpw">
              This account doesn't have a password yet. <Link to="/forgot-password" className="underline">Set one with a reset code</Link> sent to your email, then continue with Google again to connect it.
            </p>
          )}
          {linkToken && (
            <p className="text-xs mt-3" style={{ color: '#0f1d2f', opacity: 0.7 }}>
              Don't know the password? <Link to="/forgot-password" className="underline" data-testid="login-link-reset">Reset it</Link>, or <Link to="/login" className="underline" data-testid="login-link-cancel">cancel</Link> and sign in another way.
            </p>
          )}
          {paused && (
            <p className="text-sm mt-6 px-4 py-3 border text-left" style={{ color: '#0f1d2f', borderColor: 'rgba(182, 164, 109, 0.5)', backgroundColor: 'rgba(182, 164, 109, 0.08)' }} data-testid="login-paused-notice">
              {paused} <Link to="/forgot-password" className="underline" data-testid="login-paused-reset-link">Reset your password</Link>
            </p>
          )}
          {new URLSearchParams(location.search).get("expired") && (
            <p className="text-sm mt-6 px-4 py-3 border" style={{ color: '#0f1d2f', borderColor: 'rgba(182, 164, 109, 0.5)', backgroundColor: 'rgba(182, 164, 109, 0.08)' }} data-testid="login-session-expired">
              Your session has ended. Please sign in again — your Wish List and preferences are saved.
            </p>
          )}
        </div>

        {!linkToken && <GoogleButton next={requestedFrom || ""} testid="login-google-btn" />}
        {!linkToken && googleOn && (
        <div className="flex items-center gap-3 my-6" aria-hidden="true">
          <span className="flex-1 h-px" style={{ backgroundColor: 'rgba(15,29,47,0.12)' }} />
          <span className="text-[10px] tracking-widest uppercase" style={{ color: '#0f1d2f', opacity: 0.5 }}>or continue with email</span>
          <span className="flex-1 h-px" style={{ backgroundColor: 'rgba(15,29,47,0.12)' }} />
        </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-xs tracking-widest uppercase mb-2" style={{ color: '#0f1d2f' }}>
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="email"
              required
              data-testid="login-email-input"
            />
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase mb-2" style={{ color: '#0f1d2f' }}>
              Password
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              autoComplete="current-password"
              required
              data-testid="login-password-input"
            />
            <div className="mt-2 text-right">
              <Link 
                to="/forgot-password" 
                className="text-xs underline"
                style={{ color: '#0f1d2f', opacity: 0.6 }}
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>
          </div>
          <button 
            type="submit" 
            className="btn-primary w-full"
            disabled={loading}
            data-testid="login-submit-btn"
          >
            {loading ? "One moment..." : "Sign In"}
          </button>
        </form>

        <p className="text-center mt-10 text-sm" style={{ color: '#0f1d2f' }}>
          New here?{" "}
          <Link to="/signup" className="underline" data-testid="signup-link">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
